import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type CaseType, type AuditEntry, type SupportQueueRow } from '@vello/api-client';
import {
  QUEUE_STATUS_FILTERS,
  assigneeOf,
  dueInParts,
  queueStatusParam,
  slaBadge,
  type QueueStatusFilter,
  grantsAllow,
  loadCaseAudit,
  priorityTone,
  queueState,
  screenAllowed,
  looksLikeProtocol,
  ticketReference,
  ticketAge,
  ticketStatusTone,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCaseAuditText } from '../i18n/case-audit-text';
import { Badge, Button, Field, Notice, PageHeader, Panel, Select, Table, ListTable, TextInput, type Column, FormRow, SectionTitle, InlineRow, Row, Text, Stack, EmptyCell, PanelBody, Divider, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import { OpenTicketDrawer } from '../components/open-ticket-drawer';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';

/** D448 (S-02): one page of the queue (it refreshes itself while the tab is visible: `useAutoRefresh`). */
const PAGE_SIZE = 25;

/**
 * Support › Cases (S114; W2-CONSOLES D364; W5-SUPPORT-DESK D384).
 *
 * D364: the queue is **per market** — the market comes from the operator's own grants (a picker only when more than one
 * city is reachable), and the panel says which state it is in instead of an empty table that could mean four things.
 *
 * D384: the atendente handles a ticket end to end. The queue row says what the ticket is about, how old it is and who
 * works it (the priority badge now reads the server's `HIGH`/`URGENT` — it compared `'high'` and was never red); a row
 * opens the Ticket detail (subject card, internal notes, the logged phone reveal, assign/wait/resolve/close). The ONE
 * primary action here opens a ticket for a caller. Money actions moved to the detail, where the server says whether the
 * market allows them (a cash-only market does not) and in which currency — the agent never types one.
 */
export function CasesScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { toast } = useConsole();
  const { t } = useT();
  const { fail, v } = useVocab();
  const auditText = useCaseAuditText();
  const [tickets, setTickets] = useState<readonly SupportQueueRow[] | undefined>(undefined);
  const [queueError, setQueueError] = useState<string | undefined>(undefined);
  const mayView = screenAllowed(grants, 'cases');
  const mayOpen = grants === undefined || grantsAllow(grants, { permission: 'support_ticket:manage', target: 'GEO' });
  // The ticket detail needs `support_ticket:view` (a SUPPORT staff operator reads the queue without it).
  const mayOpenDetail = screenAllowed(grants, 'case_detail');
  // The queue's market is the header scope's (owner, 2026-10-12): the scoped city, or the only market the grants reach.
  const { market, choices } = useScopeMarket();
  const [newOpen, setNewOpen] = useState(false);

  const [auditType, setAuditType] = useState<CaseType>('support');
  const [auditId, setAuditId] = useState('');
  const [audit, setAudit] = useState<readonly AuditEntry[] | undefined>(undefined);
  const [auditError, setAuditError] = useState<string | undefined>(undefined);

  // D448 (S-02): the queue is the OPEN tickets by default (most urgent, then most due, first), a status filter reaches the
  // rest, and it pages; it refreshes itself (AUTO_REFRESH_MS.queue) while the tab is visible.
  const [statusFilter, setStatusFilter] = useState<QueueStatusFilter>('open');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // D856 (owner D845 Q6): the protocol being looked up (typed, then applied with Enter / Buscar) and what is applied to the queue.
  const [protocolText, setProtocolText] = useState('');
  const [protocol, setProtocol] = useState<string | undefined>(undefined);
  // A new scope market starts from page one and from "loading", never from the previous market's rows.
  useEffect(() => { setOffset(0); setTickets(undefined); }, [market]);
  const refreshQueue = useCallback(async (): Promise<void> => {
    setQueueError(undefined);
    // No market, or no permission → do not call: an un-scoped read answers an empty list that means nothing.
    if (!mayView || market === undefined || market === '') {
      setTickets(undefined);
      return;
    }
    const status = queueStatusParam(statusFilter);
    const res = await runtime.supportDesk.queue({ market, limit: PAGE_SIZE, offset, ...(status !== undefined ? { status } : {}), ...(protocol !== undefined ? { protocol } : {}) });
    if (res.ok) {
      setTickets(res.value.tickets.filter((row) => typeof row.ticketId === 'string'));
      setHasMore(res.value.page?.hasMore === true);
    } else {
      setTickets(undefined);
      setHasMore(false);
      setQueueError(res.error.code);
    }
  }, [runtime, mayView, market, statusFilter, offset, protocol]);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);
  useAutoRefresh(refreshQueue, { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && market !== undefined && market !== '' });

  const onAssign = async (ticketId: string): Promise<void> => {
    const res = await runtime.supportDesk.assignToSelf(ticketId);
    if (res.ok) await refreshQueue();
    else toast({ message: t('desk.assignFailed', { code: fail(res.error) }), icon: 'shieldAlert', tone: 'warning' });
  };

  const onAudit = async (): Promise<void> => {
    setAuditError(undefined);
    const res = await loadCaseAudit(runtime, auditType, auditId);
    if (res.ok) setAudit(res.value.entries);
    else setAuditError(t('cases.error.audit', { code: fail(res.error) }));
  };

  const now = new Date().toISOString();
  const age = (row: SupportQueueRow): string => {
    if (typeof row.createdAt !== 'string') return '—';
    const a = ticketAge(row.createdAt, now);
    return t(`desk.age.${a.unit}`, { n: String(a.value) });
  };
  const label = (prefix: 'category' | 'status', value: string | undefined): string => v(prefix === 'category' ? 'support.support_category' : 'support.ticket_status', value);

  const columns: readonly Column<SupportQueueRow>[] = [
    { key: 'id', header: t('cases.col.ticket'), render: (r) => <span className="tnum" title={r.ticketId}>{ticketReference(r)}</span> },
    { key: 'subject', header: t('desk.col.subject'), render: (r) => (r.subject !== undefined ? v('support.ticket_subject_kind', r.subject.kind) : t('desk.subject.REQUESTER')) },
    { key: 'cat', header: t('cases.col.category'), render: (r) => label('category', r.category) },
    { key: 'pri', header: t('cases.col.priority'), render: (r) => <Badge tone={priorityTone(r.priority)}>{v('support.ticket_priority', r.priority)}</Badge> },
    { key: 'st', header: t('cases.col.status'), render: (r) => <Badge tone={ticketStatusTone(r.status)}>{label('status', r.status)}</Badge> },
    { key: 'age', header: t('desk.col.age'), render: (r) => <span className="tnum">{age(r)}</span> },
    {
      // D448 (S-03): the SLA the server says the row stands at (overdue, due soon, running, met) and its escalations.
      key: 'sla',
      header: t('cases.col.sla'),
      render: (r) => {
        const b = slaBadge(r, now);
        const parts = dueInParts(b.minutes ?? 0);
        return (
          <InlineRow gap={2} align="center" wrap>
            <Badge tone={b.tone}><span className="tnum">{b.messageId === 'sla.dueIn' ? t('sla.dueIn', { time: t(`desk.age.${parts.unit}`, { n: String(parts.value) }) }) : t(b.messageId)}</span></Badge>
            {(r.slaState?.escalations ?? 0) > 0 && <Badge tone="warning">{t('sla.escalated')}</Badge>}
          </InlineRow>
        );
      },
    },
    {
      key: 'who',
      header: t('desk.col.assignee'),
      render: (r) => (
        <InlineRow gap={2} align="center" wrap>
          {t(`desk.assignee.${assigneeOf(r)}`)}
          {r.compensationRequested === true && <Badge tone="warning">{t('desk.compensationFlag')}</Badge>}
        </InlineRow>
      ),
    },
  ];

  const canOpenHere = mayView && mayOpen && market !== undefined && market !== '';
  const protocolInvalid = protocolText.trim() !== '' && !looksLikeProtocol(protocolText);
  const searchProtocol = (): void => {
    const typed = protocolText.trim();
    setOffset(0);
    setProtocol(typed === '' ? undefined : typed);
  };

  return (
    <>
      <PageHeader
        title={t('cases.title')}
        subtitle={t('cases.subtitle')}
        actions={canOpenHere ? <Button variant="primary" onClick={() => setNewOpen(true)}>{t('desk.openForCaller')}</Button> : undefined}
      />

      {mayView && (
        <Panel>
          <Field label={t('cases.filter.status')} htmlFor="cases-status" hint={t('cases.filter.hint')}>
            <Select id="cases-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as QueueStatusFilter); setOffset(0); }}>
              {QUEUE_STATUS_FILTERS.map((f) => (
                <option key={f} value={f}>{f === 'open' || f === 'all' ? t(`cases.filter.${f}`) : v('support.ticket_status', f)}</option>
              ))}
            </Select>
          </Field>
          <form
            onSubmit={(e) => { e.preventDefault(); if (!protocolInvalid) searchProtocol(); }}
          >
            <Row gap={2} align="end" wrap>
              <Field label={t('cases.search.label')} htmlFor="cases-search-protocol" {...(protocolInvalid ? { error: t('cases.search.invalid') } : {})}>
                <TextInput id="cases-search-protocol" className="tnum" autoComplete="off" value={protocolText} placeholder={t('cases.search.placeholder')} onChange={(e) => setProtocolText(e.target.value)} />
              </Field>
              <Button type="submit" variant="ghost" disabled={protocolInvalid}>{t('cases.search.submit')}</Button>
              {protocol !== undefined && <Button type="button" variant="ghost" onClick={() => { setProtocolText(''); setProtocol(undefined); setOffset(0); }}>{t('cases.search.clear')}</Button>}
            </Row>
          </form>
        </Panel>
      )}

      <Panel padded={false}>
        {(() => {
          const state = queueState({ market, mayView, ...(queueError !== undefined ? { errorCode: queueError } : {}), ...(tickets !== undefined ? { rows: tickets } : {}) });
          const note = (message: string): ReactElement => (
            <EmptyCell>{message}</EmptyCell>
          );
          if (state === 'no_permission') return note(t('cases.noPermission'));
          if (state === 'needs_market') return <PanelBody><ScopeMarketNotice choices={choices} /></PanelBody>;
          if (state === 'refused') return <PanelBody><Notice tone="danger">{t('cases.error.queue', { code: queueError ?? '' })}</Notice></PanelBody>;
          return (
            <>
              {/* The record opens on its own detail page (the ticket has one): the row navigates there. */}
              <ListTable
                columns={columns}
                rows={tickets ?? []}
                rowKey={(row) => row.ticketId}
                {...(mayOpenDetail ? { onRowClick: (row: SupportQueueRow) => navigate('case_detail', { id: row.ticketId }) } : {})}
                actions={(r) => (assigneeOf(r) === 'none' ? <Button size="sm" variant="ghost" onClick={() => void onAssign(r.ticketId)}>{t('desk.assignToMe')}</Button> : null)}
                empty={note(state === 'loading' ? t('cases.empty') : t('cases.emptyInMarket', { market: choices.find((c) => c.id === market)?.name ?? market ?? '' }))}
              />
              {(offset > 0 || hasMore) && (
                <>
                <Divider />
                <PanelBody>
                  <Row gap={2} align="center" justify="between">
                  <Text role="caption" tone="secondary" mono>
                    {t('cases.page.range', { from: String(offset + 1), to: String(offset + (tickets?.length ?? 0)) })}
                  </Text>
                  <InlineRow gap={2} align="stretch">
                    <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>{t('cases.page.prev')}</Button>
                    <Button variant="ghost" disabled={!hasMore} onClick={() => setOffset(offset + PAGE_SIZE)}>{t('cases.page.next')}</Button>
                  </InlineRow>
                </Row>
                </PanelBody>
              </>
              )}
            </>
          );
        })()}
      </Panel>

      <Panel><Stack gap={4}>
        <SectionTitle>{t('cases.audit')}</SectionTitle>
        <FormRow
          columns="minmax(140px, 180px) minmax(0, 1fr)"
          action={
            <Button variant="secondary" disabled={auditId === ''} onClick={() => void onAudit()}>{t('cases.loadAudit')}</Button>
          }
        >
          <Field label={t('cases.field.type')} htmlFor="audit-type">
            <Select id="audit-type" value={auditType} onChange={(e) => setAuditType(e.target.value as CaseType)}>
              <option value="support">support</option>
              <option value="safety">safety</option>
              <option value="risk">risk</option>
            </Select>
          </Field>
          <Field label={t('cases.field.caseId')} htmlFor="audit-id">
            <TextInput id="audit-id" value={auditId} onChange={(e) => setAuditId(e.target.value)} />
          </Field>
        </FormRow>
        {auditError !== undefined && <Notice tone="danger">{auditError}</Notice>}
        {audit !== undefined && (
          <div>
            <Table
              columns={[
                { key: 'at', header: t('cases.col.when'), render: (e: AuditEntry) => <span className="tnum">{e.at}</span> },
                { key: 'actor', header: t('cases.col.actor'), render: (e: AuditEntry) => auditText.actor(e) },
                { key: 'action', header: t('cases.col.action'), render: (e: AuditEntry) => auditText.action(e) },
                { key: 'reason', header: t('cases.col.reason'), render: (e: AuditEntry) => auditText.reason(e) },
              ]}
              rows={audit}
              rowKey={(e) => `${e.at}-${e.action}-${e.actorId}`}
              empty={<EmptyCell>{t('cases.empty.audit')}</EmptyCell>}
            />
            <Text as="p" role="caption" tone="tertiary">{t('cases.appendOnly')}</Text>
          </div>
        )}
      </Stack></Panel>

      <OpenTicketDrawer
        open={newOpen}
        market={market}
        onClose={() => setNewOpen(false)}
        onOpened={(ticketId) => {
          setNewOpen(false);
          toast({ message: t('desk.new.opened'), icon: 'check', tone: 'success' });
          navigate('case_detail', { id: ticketId });
        }}
      />
    </>
  );
}
